import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getInventoryRemovals, getInventoryAuditLog } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  Loader2,
  AlertTriangle,
  Search,
  Package,
  History as HistoryIcon,
  Trash2,
} from "lucide-react";

const TARGET_LABELS = {
  receive_history: { label: "Receive History", color: "bg-amber-100 text-amber-700" },
  correction: { label: "Correction", color: "bg-violet-100 text-violet-700" },
  marketplace_order: { label: "Marketplace Order", color: "bg-blue-100 text-blue-700" },
};

function RemovalsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const d = await getInventoryRemovals();
        setItems(Array.isArray(d) ? d : []);
      } catch {
        toast.error("Failed to load inventory removals");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      it.product_name?.toLowerCase().includes(q) ||
      it.reason?.toLowerCase().includes(q) ||
      it.removed_by?.toLowerCase().includes(q) ||
      it.source_order_number?.toLowerCase().includes(q)
    );
  });

  const totalQty = filtered.reduce((s, r) => s + (r.quantity || 0), 0);

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4 lg:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-red-50 p-2 rounded-md">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-lg text-navy">Permanent Inventory Write-Offs</h2>
            <p className="text-sm text-slate-500">
              Every time an admin cancels a marketplace order with the &ldquo;Remove from inventory&rdquo;
              option, an entry is logged here with the reason. Use this to spot patterns of damage,
              fraud, or process gaps.
            </p>
          </div>
          <div className="text-right border-l border-slate-200 pl-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Removed</p>
            <p className="text-2xl font-bold text-red-600">{totalQty}</p>
            <p className="text-xs text-slate-400">{filtered.length} entries</p>
          </div>
        </div>
        <div className="relative max-w-sm mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search product, reason, admin, order #..."
            className="pl-9"
            data-testid="removals-search"
          />
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            {items.length === 0 ? "No inventory has been written off yet." : "No removals match your search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 px-3 font-medium text-slate-500">Removed</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Product</th>
                  <th className="py-2 px-3 font-medium text-slate-500 text-center">Qty</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Reason</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Admin</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Source</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`removal-${r.id}`}>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{new Date(r.removed_at).toLocaleString()}</td>
                    <td className="py-2 px-3 font-medium text-navy">{r.product_name || "—"}</td>
                    <td className="py-2 px-3 text-center font-bold text-red-600">{r.quantity}</td>
                    <td className="py-2 px-3 text-slate-700">{r.reason}</td>
                    <td className="py-2 px-3 text-slate-600">{r.removed_by}</td>
                    <td className="py-2 px-3">
                      {r.source_order_number ? (
                        <span className="font-mono text-xs text-slate-500">{r.source} → {r.source_order_number}</span>
                      ) : (
                        <span className="text-xs text-slate-400">{r.source}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityAuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const d = await getInventoryAuditLog();
        setItems(Array.isArray(d) ? d : []);
      } catch {
        toast.error("Failed to load audit log");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((it) => {
    if (typeFilter && it.target_type !== typeFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      it.summary?.toLowerCase().includes(q) ||
      it.performed_by?.toLowerCase().includes(q) ||
      it.performed_by_email?.toLowerCase().includes(q) ||
      it.target_id?.toLowerCase().includes(q)
    );
  });

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4 lg:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-slate-100 p-2 rounded-md">
            <Trash2 className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-lg text-navy">Hard-Delete Activity</h2>
            <p className="text-sm text-slate-500">
              Every hard-delete an admin or Ray Kalasho performs on a Receive History, Correction,
              or Marketplace Order is recorded here. Use this to investigate unexpected disappearances.
            </p>
          </div>
          <div className="text-right border-l border-slate-200 pl-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Deletes</p>
            <p className="text-2xl font-bold text-navy">{filtered.length}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search admin, summary, ID..."
              className="pl-9"
              data-testid="audit-search"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="audit-type-filter"
          >
            <option value="">All types</option>
            <option value="receive_history">Receive History</option>
            <option value="correction">Correction</option>
            <option value="marketplace_order">Marketplace Order</option>
          </select>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <HistoryIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            {items.length === 0 ? "No deletions have been logged yet." : "No deletions match your search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 px-3 font-medium text-slate-500">When</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Type</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Summary</th>
                  <th className="py-2 px-3 font-medium text-slate-500">Performed By</th>
                  <th className="py-2 px-3 font-medium text-slate-500 text-xs">Target ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const cfg = TARGET_LABELS[it.target_type] || { label: it.target_type, color: "bg-slate-100 text-slate-700" };
                  return (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-${it.id}`}>
                      <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{new Date(it.performed_at).toLocaleString()}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                      </td>
                      <td className="py-2 px-3 text-navy">{it.summary || "—"}</td>
                      <td className="py-2 px-3">
                        <p className="text-slate-700">{it.performed_by}</p>
                        <p className="text-xs text-slate-400">{it.performed_by_email} · {it.performed_by_role}</p>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-400">{it.target_id?.slice(0, 8)}…</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminInventoryRemovalsReport() {
  const [tab, setTab] = useState("removals");
  return (
    <AdminLayout title="Inventory Removals" breadcrumb="Reports / Inventory Removals">
      <div className="space-y-4" data-testid="admin-inventory-removals">
        <div className="flex items-center gap-2">
          <Link to="/admin/reports">
            <Button variant="ghost" size="sm" data-testid="back-to-reports">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Reports
            </Button>
          </Link>
        </div>

        <div className="flex flex-wrap gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit" data-testid="removals-tabs">
          <button
            onClick={() => setTab("removals")}
            data-testid="tab-removals"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "removals" ? "bg-teal text-white" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Inventory Removals
          </button>
          <button
            onClick={() => setTab("audit")}
            data-testid="tab-audit"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "audit" ? "bg-teal text-white" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <HistoryIcon className="w-4 h-4" />
            Delete Activity
          </button>
        </div>

        {tab === "removals" ? <RemovalsTab /> : <ActivityAuditTab />}
      </div>
    </AdminLayout>
  );
}
